import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import TreeItem from "./TreeItem";
import { horseService } from "../../api";
import { renderNoHorsesFoundMessage } from "../../api/helpers";

const customSelectStyles = {
	control: (provided, state) => ({
		...provided,
		backgroundColor: state.isDisabled ? "#e0e0e0" : provided.backgroundColor,
		cursor: state.isDisabled ? "not-allowed" : "default",
	}),
};

const HORSE_FIELDS = {
	sire: { label: "Sire", placeholder: "Search for a sire" },
	dam: { label: "Dam", placeholder: "Search for a dam" },
	sire_sire: { label: "Paternal grandsire", placeholder: "Search for the sire's sire" },
	sire_dam: { label: "Paternal granddam", placeholder: "Search for the sire's dam" },
	dam_sire: { label: "Maternal grandsire", placeholder: "Search for the dam's sire" },
	dam_dam: { label: "Maternal granddam", placeholder: "Search for the dam's dam" },
};

function selectionId(value) {
	return Array.isArray(value) ? value[0] : value;
}

function useHorseSearch({ sponsorOnly = false, filterKey = "" } = {}) {
	const [inputValue, setInputValue] = useState("");
	const [options, setOptions] = useState([]);
	const [message, setMessage] = useState("Type at least 3 characters");

	useEffect(() => {
		setInputValue("");
		setOptions([]);
		setMessage("Type at least 3 characters");
	}, [filterKey]);

	useEffect(() => {
		const keyword = inputValue.trim();
		if (keyword.length < 3) {
			setMessage("Type at least 3 characters");
			setOptions([]);
			return undefined;
		}

		setMessage("Searching…");
		const timeout = setTimeout(() => {
			const query = { keyword };
			if (sponsorOnly !== null) query.sponsor = sponsorOnly;
			horseService.find(query).then((response) => {
				setOptions(response || []);
				setMessage(response?.length ? "Type at least 3 characters" : "No horses found");
			}).catch((error) => {
				console.error(error);
				setOptions([]);
				setMessage("Search failed — Try again");
			});
		}, 500);

		return () => clearTimeout(timeout);
	}, [inputValue, sponsorOnly]);

	return { inputValue, setInputValue, options, message };
}

function horseMeta(horse) {
	if (!horse) return "";
	const country = horse.country || horse.country_code;
	const year = horse.year || horse.birth_year || horse.foaled;
	return [country, year].filter(Boolean).join(" • ");
}

function SelectedHorseCard({ type, horse, onChange, onRemove, sponsorAction }) {
	const name = horse?.display_name || horse?.name || `Selected ${HORSE_FIELDS[type].label.toLowerCase()}`;
	const meta = horseMeta(horse);
	return (
		<div className="selected-horse-card">
			<div className="selected-horse-card__icon" aria-hidden="true">
				<i className="fas fa-horse-head" />
			</div>
			<div className="selected-horse-card__details">
				<span className="selected-horse-card__role">{HORSE_FIELDS[type].label}</span>
				<strong>{name}</strong>
				{meta && <span>{meta}</span>}
			</div>
			<div className="selected-horse-card__actions">
				{sponsorAction}
				<button type="button" className="pedigree-text-button" onClick={onChange}>Change</button>
				<button type="button" className="pedigree-text-button pedigree-text-button--danger" onClick={onRemove}>Remove</button>
			</div>
		</div>
	);
}

function PedigreeTree({
	onSelect,
	selectedHorses,
	setSponsorId,
	setIsOpenSponsor,
	setCredits,
	originalCredits,
	horse,
	setHorse,
	onAddNewHorse,
	selectionDetails = {},
	onSelectionDetailsChange,
}) {
	const [sireAncestry, setSireAncestry] = useState(null);
	const [damAncestry, setDamAncestry] = useState(null);
	const [loadingAncestry, setLoadingAncestry] = useState(false);
	const [showAllFamily, setShowAllFamily] = useState(false);
	const [editingSire, setEditingSire] = useState(false);
	const [editingDam, setEditingDam] = useState(false);
	const [manualSireParents, setManualSireParents] = useState(
		Boolean(selectionId(selectedHorses?.sire_sire) || selectionId(selectedHorses?.sire_dam))
	);
	const [manualDamParents, setManualDamParents] = useState(
		Boolean(selectionId(selectedHorses?.dam_sire) || selectionId(selectedHorses?.dam_dam))
	);
	const requestIdRef = useRef({ sire: 0, dam: 0 });
	const hydratedParentsRef = useRef({});

	const sireSearch = useHorseSearch({ sponsorOnly: null });
	const damSearch = useHorseSearch({ sponsorOnly: null });
	const sireSireSearch = useHorseSearch({ sponsorOnly: false });
	const sireDamSearch = useHorseSearch({ sponsorOnly: false });
	const damSireSearch = useHorseSearch({ sponsorOnly: false });
	const damDamSearch = useHorseSearch({ sponsorOnly: false });
	const searches = {
		sire: sireSearch,
		dam: damSearch,
		sire_sire: sireSireSearch,
		sire_dam: sireDamSearch,
		dam_sire: damSireSearch,
		dam_dam: damDamSearch,
	};

	const sireId = selectionId(selectedHorses?.sire);
	const damId = selectionId(selectedHorses?.dam);

	useEffect(() => {
		if (selectionId(selectedHorses?.sire_sire) || selectionId(selectedHorses?.sire_dam)) setManualSireParents(true);
		if (selectionId(selectedHorses?.dam_sire) || selectionId(selectedHorses?.dam_dam)) setManualDamParents(true);
	}, [selectedHorses?.sire_sire, selectedHorses?.sire_dam, selectedHorses?.dam_sire, selectedHorses?.dam_dam]);

	useEffect(() => {
		setCredits(horse?.sponsor ? 0 : originalCredits);
	}, [horse, originalCredits, setCredits]);

	async function loadAncestry(id, type) {
		const branch = type.startsWith("sire") ? "sire" : "dam";
		const requestId = ++requestIdRef.current[branch];
		setLoadingAncestry(true);
		let initialHorse = null;
		if (type === "sire_sire" || type === "dam_sire") initialHorse = "horse_sire";
		if (type === "sire_dam" || type === "dam_dam") initialHorse = "horse_dam";

		try {
			const response = await horseService.ancestry(id, initialHorse);
			if (requestId !== requestIdRef.current[branch]) return response;
			const ancestry = {};
			(response.ancestry || []).forEach((item) => {
				ancestry[item.position] = item.ancestry_horse;
			});
			if (type.startsWith("sire")) setSireAncestry(ancestry);
			if (type.startsWith("dam")) setDamAncestry(ancestry);
			return response;
		} finally {
			if (requestId === requestIdRef.current[branch]) setLoadingAncestry(false);
		}
	}

	useEffect(() => {
		[["sire", sireId], ["dam", damId]].forEach(([type, id]) => {
			if (!id || hydratedParentsRef.current[type] === id) return;
			hydratedParentsRef.current[type] = id;
			const knownHorse = type === "sire" && Number(horse?.id) === Number(id) ? horse : selectionDetails[type];
			if (knownHorse && Number(knownHorse.id) === Number(id)) {
				onSelectionDetailsChange?.(type, knownHorse);
			} else {
				horseService.retrieve(id).then((response) => {
					onSelectionDetailsChange?.(type, response);
					if (type === "sire") setHorse(response);
				}).catch((error) => console.error(error));
			}
			loadAncestry(id, type).catch((error) => console.error(error));
		});
	}, [sireId, damId, horse, onSelectionDetailsChange, selectionDetails, setHorse]);

	async function selectHorse(type, selectedHorse) {
		if (!selectedHorse || loadingAncestry) return;
		try {
			const response = await loadAncestry(selectedHorse.id, type);
			if (type === "sire" || type === "dam") {
				hydratedParentsRef.current[type] = selectedHorse.id;
			}
			onSelect(response?.id || selectedHorse.id, type);
			onSelectionDetailsChange?.(type, selectedHorse);
			if (type === "sire") {
				setHorse(selectedHorse);
				setEditingSire(false);
				setManualSireParents(false);
			}
			if (type === "dam") {
				setEditingDam(false);
				setManualDamParents(false);
			}
			searches[type].setInputValue("");
		} catch (error) {
			console.error(error);
		}
	}

	function removeHorse(type) {
		const branch = type.startsWith("sire") ? "sire" : "dam";
		requestIdRef.current[branch] += 1;
		onSelect(null, type);
		onSelectionDetailsChange?.(type, null);
		if (type === "sire") {
			setHorse(null);
			setSireAncestry(null);
			setEditingSire(false);
			delete hydratedParentsRef.current.sire;
		}
		if (type === "dam") {
			setDamAncestry(null);
			setEditingDam(false);
			delete hydratedParentsRef.current.dam;
		}
	}

	function renderSelect(type, autoFocus = false) {
		const search = searches[type];
		const selectedId = selectionId(selectedHorses?.[type]);
		const selectedDetail = selectionDetails[type];
		const selectedValue = selectedId
			? { value: selectedId, label: selectedDetail?.display_name || selectedDetail?.name || `Horse #${selectedId}` }
			: null;
		return (
			<div
				className="pedigree-horse-select"
				data-tour-id={type === "sire" || type === "dam" ? `pedigree-${type}-input` : undefined}
			>
				<label htmlFor={`pedigree-${type}`}>{HORSE_FIELDS[type].label}</label>
				<Select
					inputId={`pedigree-${type}`}
					options={search.options.map((item) => ({ value: item.id, label: item.display_name, horse: item }))}
					value={selectedValue}
					onChange={(option) => option ? selectHorse(type, option.horse) : removeHorse(type)}
					inputValue={search.inputValue}
					onInputChange={(value, { action }) => {
						if (action === "input-change" || action === "set-value") search.setInputValue(value);
					}}
					isDisabled={loadingAncestry}
					styles={customSelectStyles}
					className="react-select-container"
					classNamePrefix="react-select"
					placeholder={HORSE_FIELDS[type].placeholder}
					noOptionsMessage={() => renderNoHorsesFoundMessage(search.message, onAddNewHorse)}
					isClearable
					autoFocus={autoFocus}
				/>
			</div>
		);
	}

	function sponsorButton() {
		if (!horse?.sponsor) return null;
		return (
			<button
				type="button"
				className="pedigree-icon-button"
				onClick={() => {
					setSponsorId(horse.sponsor.id);
					setIsOpenSponsor(true);
				}}
				aria-label="View sponsor details"
				title="View sponsor details"
			>
				<i className="fas fa-award" aria-hidden="true" />
			</button>
		);
	}

	function renderParentPanel(type) {
		const isSire = type === "sire";
		const parentId = isSire ? sireId : damId;
		const details = isSire
			? (selectionDetails.sire || (Number(horse?.id) === Number(sireId) ? horse : null))
			: selectionDetails.dam;
		const editing = isSire ? editingSire : editingDam;
		const manual = isSire ? manualSireParents : manualDamParents;
		const setEditing = isSire ? setEditingSire : setEditingDam;
		const setManual = isSire ? setManualSireParents : setManualDamParents;
		const grandparentTypes = isSire ? ["sire_sire", "sire_dam"] : ["dam_sire", "dam_dam"];

		return (
			<section className="pedigree-parent-panel" aria-labelledby={`${type}-panel-title`}>
				<div className="pedigree-parent-panel__heading">
					<div>
						<span className="pedigree-parent-panel__step">{isSire ? "Paternal line" : "Maternal line"}</span>
						<h3 id={`${type}-panel-title`}>{HORSE_FIELDS[type].label}</h3>
					</div>
					<span className={`pedigree-status-chip ${parentId ? "is-complete" : ""}`}>
						{parentId ? "Selected" : manual ? "Using grandparents" : "Required"}
					</span>
				</div>

				{parentId && !editing ? (
					<SelectedHorseCard
						type={type}
						horse={details}
						onChange={() => setEditing(true)}
						onRemove={() => removeHorse(type)}
						sponsorAction={isSire ? sponsorButton() : null}
					/>
				) : (
					<>
						{renderSelect(type, editing)}
						{editing && parentId && (
							<button type="button" className="pedigree-text-button" onClick={() => setEditing(false)}>Cancel change</button>
						)}
					</>
				)}

				{!parentId && (
					<div className="pedigree-manual-parents">
						<button
							type="button"
							className="pedigree-disclosure-button"
							onClick={() => setManual(!manual)}
							aria-expanded={manual}
							aria-controls={`${type}-grandparents`}
						>
							<i className={`fas fa-chevron-${manual ? "up" : "down"}`} aria-hidden="true" />
							I don't know the {type} — add grandparents instead
						</button>
						{manual && (
							<div id={`${type}-grandparents`} className="pedigree-grandparents-grid">
								{grandparentTypes.map((grandparentType) => (
									<React.Fragment key={grandparentType}>{renderSelect(grandparentType)}</React.Fragment>
								))}
							</div>
						)}
					</div>
				)}
			</section>
		);
	}

	function renderFamilyBranch(title, ancestry) {
		const generations = [
			[1, 2],
			[3, 4, 5, 6],
			Array.from({ length: 8 }, (_, i) => i + 7),
			Array.from({ length: 16 }, (_, i) => i + 15),
		];
		return (
			<section className="pedigree-family-branch">
				<h4>{title}</h4>
				<div className="pedigree-family-grid">
					{generations.map((positions, index) => (
						<div className="tree__col" key={index}>
							{positions.map((position) => (
								<TreeItem key={position} value={ancestry?.[position]?.display_name || ""} />
							))}
						</div>
					))}
				</div>
			</section>
		);
	}

	return (
		<div className="pedigree-parent-builder">
			<div className="pedigree-parent-grid">
				{renderParentPanel("sire")}
				{renderParentPanel("dam")}
			</div>

			{(sireId || damId) && (
				<div className="pedigree-family-disclosure">
					<button type="button" className="button outline" onClick={() => setShowAllFamily(!showAllFamily)} aria-expanded={showAllFamily}>
						<i className="far fa-eye mr-10" aria-hidden="true" />
						{showAllFamily ? "Hide extended family" : "See all family"}
					</button>
				</div>
			)}

			{showAllFamily && (
				<div className="pedigree-family-view">
					{sireId && renderFamilyBranch("Sire family", sireAncestry)}
					{damId && renderFamilyBranch("Dam family", damAncestry)}
				</div>
			)}
		</div>
	);
}

export default PedigreeTree;
